import {useCallback, useMemo, useSyncExternalStore} from 'react'

import {useBoardFocusGrid, useBoardFocusStore} from './contexts'
import type {BoardFocusAxis, BoardFocusPosition, BoardFocusStep} from './types'

export type UseBoardFocusResult = {
  /**
   * Where focus is, as a column, a row and a card. `null` only on a board with no columns
   * or no rows, which has nowhere to put it.
   *
   * A position, not an element. It can name a card whose column the board's window has
   * scrolled past, which is exactly why it is held this way.
   */
  position: BoardFocusPosition | null
  /**
   * Move one step along one axis. Clamps at the ends of the board rather than wrapping,
   * and the element at the new position takes focus once it is rendered.
   */
  moveFocus: (axis: BoardFocusAxis, step: BoardFocusStep) => void
  /**
   * Put focus at a stated position. A position naming a column or row this board does not
   * hold is ignored; one naming a card the cell does not hold lands on the cell.
   */
  focusPosition: (position: BoardFocusPosition) => void
}

/**
 * The board's focus position, and the two ways to move it.
 *
 * The board already moves focus for the arrow keys itself, through `useBoardRoot`. This
 * is for everything that moves focus for another reason: a card taking focus after the
 * one that had it was closed, a control putting a reader somewhere specific.
 */
export function useBoardFocus(): UseBoardFocusResult {
  const {dispatch, getState, subscribe} = useBoardFocusStore()
  const grid = useBoardFocusGrid()

  // The position object itself, which the reducer keeps identical between moves, so a
  // caller re-renders when focus moves and not when anything else about it does.
  const getPosition = useCallback(() => getState().position, [getState])
  const position = useSyncExternalStore(subscribe, getPosition, getPosition)

  const moveFocus = useCallback(
    (axis: BoardFocusAxis, step: BoardFocusStep) => {
      dispatch({type: 'move', grid, axis, step})
    },
    [dispatch, grid],
  )

  const focusPosition = useCallback(
    (next: BoardFocusPosition) => {
      dispatch({type: 'focus', grid, position: next})
    },
    [dispatch, grid],
  )

  return useMemo(() => ({position, moveFocus, focusPosition}), [position, moveFocus, focusPosition])
}
